import { Role } from "./Role";
import {
  RoleConfiguration,
  createDefaultRoleConfiguration,
} from "./RoleConfiguration";
import { UserCapabilityOption } from "./UserCapabilityOption";
import { UserCapability } from "./UserCapability";

/**
 * Role-settings types and factory defaults.
 *
 * Deliberately split out of `RoleSettings.ts` to break an import cycle:
 * `RoleSettings.ts` imports `RoleSettingsModule`, and the Vuex module calls
 * `createDefaultRoleSettings()` in a class-field initializer, i.e. while the module graph is still
 * evaluating. Whenever `RoleSettings.ts` was reached first, the module got a half-initialized
 * binding and threw `createDefaultRoleSettings is not a function`.
 *
 * This module must stay a leaf with respect to the store: **do not import anything from
 * `@/store` here.**
 */

/**
 * Interface defining the mapping of roles to their configurations.
 */
export type RoleToConfigurationMap = { [key in Role]: RoleConfiguration };

/**
 * Interface defining the settings for user roles.
 */
export interface RoleSettings {
  roleToConfiguration: RoleToConfigurationMap;
}

export function getRoleConfigurationForRole(
  roleSettings: RoleSettings,
  role: Role
): RoleConfiguration {
  return roleSettings.roleToConfiguration[role];
}

export function getCapabilitiesThatRequireBasicLicence() {
  return [
    UserCapability.DAMAGE_ASSESSMENT_AND_LEAK_DETECTION,
    UserCapability.DRYING_AND_REPAIR,

    UserCapability.SEE_ALL_PROJECTS,
    UserCapability.CREATE_PROJECT,
    UserCapability.DELETE_PROJECT,
    UserCapability.REOPEN_COMPLETED_PHASES,
    UserCapability.EDIT_PROJECT,

    UserCapability.ASSIGN_PROJECT_TO_SELF,
    UserCapability.ASSIGN_PROJECT_TO_OTHERS,
    UserCapability.PROJECT_SUPERVISION,

    UserCapability.LEAK_DETECTION,
    UserCapability.DAMAGE_ASSESSMENT_LARGE,
    UserCapability.DRYING,
    UserCapability.REPAIR,
    UserCapability.HAS_VEHICLE,
  ];
}

/**
 * Function to create default role settings.
 * @returns {RoleSettings} - The default role settings.
 */
export function createDefaultRoleSettings(): RoleSettings {
  return {
    roleToConfiguration: {
      [Role.ADMIN]: createDefaultRoleConfiguration({
        capabilityMap: {
          [UserCapability.EDIT_ADMIN]: UserCapabilityOption.YES,
          [UserCapability.EDIT_USER]: UserCapabilityOption.YES,
          [UserCapability.EDIT_VALIDATION]: UserCapabilityOption.YES,
          [UserCapability.SEE_ALL_SETTINGS]: UserCapabilityOption.YES,

          [UserCapability.BASIC_LICENCE]: UserCapabilityOption.YES,
          [UserCapability.DAMAGE_ASSESSMENT_AND_LEAK_DETECTION]:
            UserCapabilityOption.YES,
          [UserCapability.DRYING_AND_REPAIR]: UserCapabilityOption.YES,
          [UserCapability.OFFICE]: UserCapabilityOption.YES,
          [UserCapability.PROJECT_MANAGEMENT]: UserCapabilityOption.YES,
          
          [UserCapability.SEE_ALL_PROJECTS]: UserCapabilityOption.NO,
          [UserCapability.CREATE_PROJECT]: UserCapabilityOption.NO,
          [UserCapability.DELETE_PROJECT]: UserCapabilityOption.NO,
          [UserCapability.REOPEN_COMPLETED_PHASES]: UserCapabilityOption.NO,
          [UserCapability.EDIT_PROJECT]: UserCapabilityOption.NO,
          
          [UserCapability.ASSIGN_PROJECT_TO_SELF]: UserCapabilityOption.YES,
          [UserCapability.ASSIGN_PROJECT_TO_OTHERS]: UserCapabilityOption.YES,
          [UserCapability.PROJECT_SUPERVISION]: UserCapabilityOption.YES,
          
          [UserCapability.EDIT_DEVICE_SETTINGS]: UserCapabilityOption.YES,
          [UserCapability.IMPORT_DEVICES]: UserCapabilityOption.YES,
          [UserCapability.EDIT_DEVICES]: UserCapabilityOption.YES,
          
          [UserCapability.LEAK_DETECTION]: UserCapabilityOption.CONDITIONAL,
          [UserCapability.DAMAGE_ASSESSMENT_LARGE]: UserCapabilityOption.CONDITIONAL,
          [UserCapability.DRYING]: UserCapabilityOption.CONDITIONAL,
          [UserCapability.REPAIR]: UserCapabilityOption.CONDITIONAL,
          [UserCapability.HAS_VEHICLE]: UserCapabilityOption.NO,

          [UserCapability.CLIENT_ADMINISTRATION]: UserCapabilityOption.NO,
          [UserCapability.DEVICE_ADMINISTRATION]: UserCapabilityOption.NO,
          [UserCapability.SETTINGS_ADMINISTRATION]: UserCapabilityOption.NO,

          [UserCapability.RATE_DRYINGS]: UserCapabilityOption.NO,
          [UserCapability.MANAGE_PRODUCTION]: UserCapabilityOption.NO,
          [UserCapability.MANAGE_CLIENTS]: UserCapabilityOption.NO,
        },
      }),
      [Role.OFFICE]: createDefaultRoleConfiguration({
        capabilityMap: {
          [UserCapability.EDIT_ADMIN]: UserCapabilityOption.NO,
          [UserCapability.EDIT_USER]: UserCapabilityOption.NO,
          [UserCapability.EDIT_VALIDATION]: UserCapabilityOption.NO,
          [UserCapability.SEE_ALL_SETTINGS]: UserCapabilityOption.NO,
          
          [UserCapability.BASIC_LICENCE]: UserCapabilityOption.YES,
          [UserCapability.DAMAGE_ASSESSMENT_AND_LEAK_DETECTION]:
            UserCapabilityOption.NO,
          [UserCapability.DRYING_AND_REPAIR]: UserCapabilityOption.NO,
          [UserCapability.OFFICE]: UserCapabilityOption.YES,
          [UserCapability.PROJECT_MANAGEMENT]: UserCapabilityOption.NO,
          
          [UserCapability.SEE_ALL_PROJECTS]: UserCapabilityOption.YES,
          [UserCapability.CREATE_PROJECT]: UserCapabilityOption.YES,
          [UserCapability.DELETE_PROJECT]: UserCapabilityOption.YES,
          [UserCapability.REOPEN_COMPLETED_PHASES]: UserCapabilityOption.NO,
          [UserCapability.EDIT_PROJECT]: UserCapabilityOption.YES,
          
          [UserCapability.ASSIGN_PROJECT_TO_SELF]: UserCapabilityOption.YES,
          [UserCapability.ASSIGN_PROJECT_TO_OTHERS]: UserCapabilityOption.YES,
          [UserCapability.PROJECT_SUPERVISION]: UserCapabilityOption.YES,
          
          [UserCapability.EDIT_DEVICE_SETTINGS]: UserCapabilityOption.NO,
          [UserCapability.IMPORT_DEVICES]: UserCapabilityOption.YES,
          [UserCapability.EDIT_DEVICES]: UserCapabilityOption.YES,
          
          [UserCapability.LEAK_DETECTION]: UserCapabilityOption.NO,
          [UserCapability.DAMAGE_ASSESSMENT_LARGE]: UserCapabilityOption.NO,
          [UserCapability.DRYING]: UserCapabilityOption.NO,
          [UserCapability.REPAIR]: UserCapabilityOption.NO,
          [UserCapability.HAS_VEHICLE]: UserCapabilityOption.NO,
          
          [UserCapability.CLIENT_ADMINISTRATION]: UserCapabilityOption.NO,
          [UserCapability.DEVICE_ADMINISTRATION]: UserCapabilityOption.NO,
          [UserCapability.SETTINGS_ADMINISTRATION]: UserCapabilityOption.NO,

          [UserCapability.RATE_DRYINGS]: UserCapabilityOption.NO,
          [UserCapability.MANAGE_PRODUCTION]: UserCapabilityOption.NO,
          [UserCapability.MANAGE_CLIENTS]: UserCapabilityOption.NO,
        },
      }),
      [Role.PROJECT_MANAGER]: createDefaultRoleConfiguration({
        capabilityMap: {
          [UserCapability.EDIT_ADMIN]: UserCapabilityOption.NO,
          [UserCapability.EDIT_USER]: UserCapabilityOption.NO,
          [UserCapability.EDIT_VALIDATION]: UserCapabilityOption.YES,
          [UserCapability.SEE_ALL_SETTINGS]: UserCapabilityOption.NO,
          
          [UserCapability.BASIC_LICENCE]: UserCapabilityOption.YES,
          [UserCapability.DAMAGE_ASSESSMENT_AND_LEAK_DETECTION]:
            UserCapabilityOption.YES,
          [UserCapability.DRYING_AND_REPAIR]: UserCapabilityOption.YES,
          [UserCapability.OFFICE]: UserCapabilityOption.NO,
          [UserCapability.PROJECT_MANAGEMENT]: UserCapabilityOption.YES,
          
          [UserCapability.SEE_ALL_PROJECTS]: UserCapabilityOption.YES,
          [UserCapability.CREATE_PROJECT]: UserCapabilityOption.NO,
          [UserCapability.DELETE_PROJECT]: UserCapabilityOption.YES,
          [UserCapability.REOPEN_COMPLETED_PHASES]: UserCapabilityOption.NO,
          [UserCapability.EDIT_PROJECT]: UserCapabilityOption.YES,
          
          [UserCapability.ASSIGN_PROJECT_TO_SELF]: UserCapabilityOption.YES,
          [UserCapability.ASSIGN_PROJECT_TO_OTHERS]: UserCapabilityOption.YES,
          [UserCapability.PROJECT_SUPERVISION]: UserCapabilityOption.YES,
          
          [UserCapability.EDIT_DEVICE_SETTINGS]: UserCapabilityOption.NO,
          [UserCapability.IMPORT_DEVICES]: UserCapabilityOption.YES,
          [UserCapability.EDIT_DEVICES]: UserCapabilityOption.YES,
          
          [UserCapability.LEAK_DETECTION]: UserCapabilityOption.CONDITIONAL,
          [UserCapability.DAMAGE_ASSESSMENT_LARGE]: UserCapabilityOption.YES,
          [UserCapability.DRYING]: UserCapabilityOption.CONDITIONAL,
          [UserCapability.REPAIR]: UserCapabilityOption.CONDITIONAL,
          [UserCapability.HAS_VEHICLE]: UserCapabilityOption.YES,
          
          [UserCapability.CLIENT_ADMINISTRATION]: UserCapabilityOption.NO,
          [UserCapability.DEVICE_ADMINISTRATION]: UserCapabilityOption.NO,
          [UserCapability.SETTINGS_ADMINISTRATION]: UserCapabilityOption.NO,

          [UserCapability.RATE_DRYINGS]: UserCapabilityOption.NO,
          [UserCapability.MANAGE_PRODUCTION]: UserCapabilityOption.NO,
          [UserCapability.MANAGE_CLIENTS]: UserCapabilityOption.NO,
        },
      }),
      [Role.LEAK_DETECTION]: createDefaultRoleConfiguration({
        capabilityMap: {
          [UserCapability.EDIT_ADMIN]: UserCapabilityOption.NO,
          [UserCapability.EDIT_USER]: UserCapabilityOption.NO,
          [UserCapability.EDIT_VALIDATION]: UserCapabilityOption.YES,
          [UserCapability.SEE_ALL_SETTINGS]: UserCapabilityOption.NO,
          
          [UserCapability.BASIC_LICENCE]: UserCapabilityOption.YES,
          [UserCapability.DAMAGE_ASSESSMENT_AND_LEAK_DETECTION]:
            UserCapabilityOption.YES,
          [UserCapability.DRYING_AND_REPAIR]: UserCapabilityOption.NO,
          [UserCapability.OFFICE]: UserCapabilityOption.NO,
          [UserCapability.PROJECT_MANAGEMENT]: UserCapabilityOption.NO,
          
          [UserCapability.SEE_ALL_PROJECTS]: UserCapabilityOption.NO,
          [UserCapability.CREATE_PROJECT]: UserCapabilityOption.NO,
          [UserCapability.DELETE_PROJECT]: UserCapabilityOption.YES,
          [UserCapability.REOPEN_COMPLETED_PHASES]: UserCapabilityOption.NO,
          [UserCapability.EDIT_PROJECT]: UserCapabilityOption.NO,
          
          [UserCapability.ASSIGN_PROJECT_TO_SELF]: UserCapabilityOption.NO,
          [UserCapability.ASSIGN_PROJECT_TO_OTHERS]: UserCapabilityOption.NO,
          [UserCapability.PROJECT_SUPERVISION]: UserCapabilityOption.NO,
          
          [UserCapability.EDIT_DEVICE_SETTINGS]: UserCapabilityOption.NO,
          [UserCapability.IMPORT_DEVICES]: UserCapabilityOption.NO,
          [UserCapability.EDIT_DEVICES]: UserCapabilityOption.NO,
          
          [UserCapability.LEAK_DETECTION]: UserCapabilityOption.YES,
          [UserCapability.DAMAGE_ASSESSMENT_LARGE]: UserCapabilityOption.NO,
          [UserCapability.DRYING]: UserCapabilityOption.NO,
          [UserCapability.REPAIR]: UserCapabilityOption.NO,
          [UserCapability.HAS_VEHICLE]: UserCapabilityOption.YES,
          
          [UserCapability.CLIENT_ADMINISTRATION]: UserCapabilityOption.NO,
          [UserCapability.DEVICE_ADMINISTRATION]: UserCapabilityOption.NO,
          [UserCapability.SETTINGS_ADMINISTRATION]: UserCapabilityOption.NO,

          [UserCapability.RATE_DRYINGS]: UserCapabilityOption.NO,
          [UserCapability.MANAGE_PRODUCTION]: UserCapabilityOption.NO,
          [UserCapability.MANAGE_CLIENTS]: UserCapabilityOption.NO,
        },
      }),
      [Role.TECHNICIAN_1]: createDefaultRoleConfiguration({
        capabilityMap: {
          [UserCapability.EDIT_ADMIN]: UserCapabilityOption.NO,
          [UserCapability.EDIT_USER]: UserCapabilityOption.NO,
          [UserCapability.EDIT_VALIDATION]: UserCapabilityOption.NO,
          [UserCapability.SEE_ALL_SETTINGS]: UserCapabilityOption.NO,
          
          [UserCapability.BASIC_LICENCE]: UserCapabilityOption.YES,
          [UserCapability.DAMAGE_ASSESSMENT_AND_LEAK_DETECTION]:
            UserCapabilityOption.NO,
          [UserCapability.DRYING_AND_REPAIR]: UserCapabilityOption.YES,
          [UserCapability.OFFICE]: UserCapabilityOption.NO,
          [UserCapability.PROJECT_MANAGEMENT]: UserCapabilityOption.NO,
          
          [UserCapability.SEE_ALL_PROJECTS]: UserCapabilityOption.NO,
          [UserCapability.CREATE_PROJECT]: UserCapabilityOption.NO,
          [UserCapability.DELETE_PROJECT]: UserCapabilityOption.NO,
          [UserCapability.REOPEN_COMPLETED_PHASES]: UserCapabilityOption.NO,
          [UserCapability.EDIT_PROJECT]: UserCapabilityOption.YES,
          
          [UserCapability.ASSIGN_PROJECT_TO_SELF]: UserCapabilityOption.NO,
          [UserCapability.ASSIGN_PROJECT_TO_OTHERS]: UserCapabilityOption.NO,
          [UserCapability.PROJECT_SUPERVISION]: UserCapabilityOption.NO,
          
          [UserCapability.EDIT_DEVICE_SETTINGS]: UserCapabilityOption.NO,
          [UserCapability.IMPORT_DEVICES]: UserCapabilityOption.NO,
          [UserCapability.EDIT_DEVICES]: UserCapabilityOption.NO,
          
          [UserCapability.LEAK_DETECTION]: UserCapabilityOption.NO,
          [UserCapability.DAMAGE_ASSESSMENT_LARGE]: UserCapabilityOption.CONDITIONAL,
          [UserCapability.DRYING]: UserCapabilityOption.YES,
          [UserCapability.REPAIR]: UserCapabilityOption.CONDITIONAL,
          [UserCapability.HAS_VEHICLE]: UserCapabilityOption.YES,
          
          [UserCapability.CLIENT_ADMINISTRATION]: UserCapabilityOption.NO,
          [UserCapability.DEVICE_ADMINISTRATION]: UserCapabilityOption.NO,
          [UserCapability.SETTINGS_ADMINISTRATION]: UserCapabilityOption.NO,

          [UserCapability.RATE_DRYINGS]: UserCapabilityOption.NO,
          [UserCapability.MANAGE_PRODUCTION]: UserCapabilityOption.NO,
          [UserCapability.MANAGE_CLIENTS]: UserCapabilityOption.NO,
        },
      }),
      [Role.TECHNICIAN_2]: createDefaultRoleConfiguration({
        capabilityMap: {
          [UserCapability.EDIT_ADMIN]: UserCapabilityOption.NO,
          [UserCapability.EDIT_USER]: UserCapabilityOption.NO,
          [UserCapability.EDIT_VALIDATION]: UserCapabilityOption.NO,
          [UserCapability.SEE_ALL_SETTINGS]: UserCapabilityOption.NO,
          
          [UserCapability.BASIC_LICENCE]: UserCapabilityOption.YES,
          [UserCapability.DAMAGE_ASSESSMENT_AND_LEAK_DETECTION]:
            UserCapabilityOption.NO,
          [UserCapability.DRYING_AND_REPAIR]: UserCapabilityOption.YES,
          [UserCapability.OFFICE]: UserCapabilityOption.NO,
          [UserCapability.PROJECT_MANAGEMENT]: UserCapabilityOption.NO,
          
          [UserCapability.SEE_ALL_PROJECTS]: UserCapabilityOption.NO,
          [UserCapability.CREATE_PROJECT]: UserCapabilityOption.NO,
          [UserCapability.DELETE_PROJECT]: UserCapabilityOption.NO,
          [UserCapability.REOPEN_COMPLETED_PHASES]: UserCapabilityOption.NO,
          [UserCapability.EDIT_PROJECT]: UserCapabilityOption.NO,
          
          [UserCapability.ASSIGN_PROJECT_TO_SELF]: UserCapabilityOption.NO,
          [UserCapability.ASSIGN_PROJECT_TO_OTHERS]: UserCapabilityOption.NO,
          [UserCapability.PROJECT_SUPERVISION]: UserCapabilityOption.NO,
          
          [UserCapability.EDIT_DEVICE_SETTINGS]: UserCapabilityOption.NO,
          [UserCapability.IMPORT_DEVICES]: UserCapabilityOption.NO,
          [UserCapability.EDIT_DEVICES]: UserCapabilityOption.NO,
          
          [UserCapability.LEAK_DETECTION]: UserCapabilityOption.NO,
          [UserCapability.DAMAGE_ASSESSMENT_LARGE]: UserCapabilityOption.NO,
          [UserCapability.DRYING]: UserCapabilityOption.YES,
          [UserCapability.REPAIR]: UserCapabilityOption.CONDITIONAL,
          [UserCapability.HAS_VEHICLE]: UserCapabilityOption.NO,
          
          [UserCapability.CLIENT_ADMINISTRATION]: UserCapabilityOption.NO,
          [UserCapability.DEVICE_ADMINISTRATION]: UserCapabilityOption.NO,
          [UserCapability.SETTINGS_ADMINISTRATION]: UserCapabilityOption.NO,

          [UserCapability.RATE_DRYINGS]: UserCapabilityOption.NO,
          [UserCapability.MANAGE_PRODUCTION]: UserCapabilityOption.NO,
          [UserCapability.MANAGE_CLIENTS]: UserCapabilityOption.NO,
        },
      }),
      [Role.TECHNICIAN_AND_LEAK_DETECTION]: createDefaultRoleConfiguration({
        capabilityMap: {
          [UserCapability.EDIT_ADMIN]: UserCapabilityOption.NO,
          [UserCapability.EDIT_USER]: UserCapabilityOption.NO,
          [UserCapability.EDIT_VALIDATION]: UserCapabilityOption.YES,
          [UserCapability.SEE_ALL_SETTINGS]: UserCapabilityOption.NO,
          
          [UserCapability.BASIC_LICENCE]: UserCapabilityOption.YES,
          [UserCapability.DAMAGE_ASSESSMENT_AND_LEAK_DETECTION]:
            UserCapabilityOption.YES,
          [UserCapability.DRYING_AND_REPAIR]: UserCapabilityOption.YES,
          [UserCapability.OFFICE]: UserCapabilityOption.NO,
          [UserCapability.PROJECT_MANAGEMENT]: UserCapabilityOption.YES,
          
          [UserCapability.SEE_ALL_PROJECTS]: UserCapabilityOption.YES,
          [UserCapability.CREATE_PROJECT]: UserCapabilityOption.NO,
          [UserCapability.DELETE_PROJECT]: UserCapabilityOption.YES,
          [UserCapability.REOPEN_COMPLETED_PHASES]: UserCapabilityOption.NO,
          [UserCapability.EDIT_PROJECT]: UserCapabilityOption.YES,
          
          [UserCapability.ASSIGN_PROJECT_TO_SELF]: UserCapabilityOption.YES,
          [UserCapability.ASSIGN_PROJECT_TO_OTHERS]: UserCapabilityOption.YES,
          [UserCapability.PROJECT_SUPERVISION]: UserCapabilityOption.YES,
          
          [UserCapability.EDIT_DEVICE_SETTINGS]: UserCapabilityOption.NO,
          [UserCapability.IMPORT_DEVICES]: UserCapabilityOption.NO,
          [UserCapability.EDIT_DEVICES]: UserCapabilityOption.NO,
          
          [UserCapability.LEAK_DETECTION]: UserCapabilityOption.CONDITIONAL,
          [UserCapability.DAMAGE_ASSESSMENT_LARGE]: UserCapabilityOption.CONDITIONAL,
          [UserCapability.DRYING]: UserCapabilityOption.CONDITIONAL,
          [UserCapability.REPAIR]: UserCapabilityOption.NO,
          [UserCapability.HAS_VEHICLE]: UserCapabilityOption.YES,
          
          [UserCapability.CLIENT_ADMINISTRATION]: UserCapabilityOption.NO,
          [UserCapability.DEVICE_ADMINISTRATION]: UserCapabilityOption.NO,
          [UserCapability.SETTINGS_ADMINISTRATION]: UserCapabilityOption.NO,

          [UserCapability.RATE_DRYINGS]: UserCapabilityOption.NO,
          [UserCapability.MANAGE_PRODUCTION]: UserCapabilityOption.NO,
          [UserCapability.MANAGE_CLIENTS]: UserCapabilityOption.NO,
        },
      }),
      [Role.CUSTOM_ROLE_1]: createDefaultRoleConfiguration({
        roleActive: false,
      }),
      [Role.CUSTOM_ROLE_2]: createDefaultRoleConfiguration({
        roleActive: false,
      }),
      [Role.CUSTOM_ROLE_3]: createDefaultRoleConfiguration({
        roleActive: false,
      }),
      [Role.CUSTOM_ROLE_4]: createDefaultRoleConfiguration({
        roleActive: false,
      }),
      [Role.CUSTOM_ROLE_5]: createDefaultRoleConfiguration({
        capabilityMap: {
          [UserCapability.EDIT_ADMIN]: UserCapabilityOption.NO,
          [UserCapability.EDIT_USER]: UserCapabilityOption.NO,
          [UserCapability.EDIT_VALIDATION]: UserCapabilityOption.NO,
          [UserCapability.SEE_ALL_SETTINGS]: UserCapabilityOption.NO,
          
          [UserCapability.BASIC_LICENCE]: UserCapabilityOption.NO,
          [UserCapability.DAMAGE_ASSESSMENT_AND_LEAK_DETECTION]:
            UserCapabilityOption.NO,
          [UserCapability.DRYING_AND_REPAIR]: UserCapabilityOption.NO,
          [UserCapability.OFFICE]: UserCapabilityOption.NO,
          [UserCapability.PROJECT_MANAGEMENT]: UserCapabilityOption.NO,
          
          [UserCapability.SEE_ALL_PROJECTS]: UserCapabilityOption.NO,
          [UserCapability.CREATE_PROJECT]: UserCapabilityOption.NO,
          [UserCapability.DELETE_PROJECT]: UserCapabilityOption.NO,
          [UserCapability.REOPEN_COMPLETED_PHASES]: UserCapabilityOption.NO,
          [UserCapability.EDIT_PROJECT]: UserCapabilityOption.NO,
          
          [UserCapability.ASSIGN_PROJECT_TO_SELF]: UserCapabilityOption.NO,
          [UserCapability.ASSIGN_PROJECT_TO_OTHERS]: UserCapabilityOption.NO,
          [UserCapability.PROJECT_SUPERVISION]: UserCapabilityOption.NO,
          
          [UserCapability.EDIT_DEVICE_SETTINGS]: UserCapabilityOption.NO,
          [UserCapability.IMPORT_DEVICES]: UserCapabilityOption.NO,
          [UserCapability.EDIT_DEVICES]: UserCapabilityOption.YES,
          
          [UserCapability.LEAK_DETECTION]: UserCapabilityOption.NO,
          [UserCapability.DAMAGE_ASSESSMENT_LARGE]: UserCapabilityOption.NO,
          [UserCapability.DRYING]: UserCapabilityOption.NO,
          [UserCapability.REPAIR]: UserCapabilityOption.NO,
          [UserCapability.HAS_VEHICLE]: UserCapabilityOption.NO,
          
          [UserCapability.CLIENT_ADMINISTRATION]: UserCapabilityOption.NO,
          [UserCapability.DEVICE_ADMINISTRATION]: UserCapabilityOption.NO,
          [UserCapability.SETTINGS_ADMINISTRATION]: UserCapabilityOption.NO,

          [UserCapability.RATE_DRYINGS]: UserCapabilityOption.NO,
          [UserCapability.MANAGE_PRODUCTION]: UserCapabilityOption.NO,
          [UserCapability.MANAGE_CLIENTS]: UserCapabilityOption.NO,
        },
      }),
    },
  };
}
